/**
 * Field Journal — the story-quest panel (design/gdd/story-quests.md).
 *
 * Same factory shape as `vendor-ui.ts`/`world-map-ui.ts`: create / open /
 * close / dispose, backdrop-click and Escape to dismiss, Salvage Codex classes
 * and tokens rather than hex literals.
 *
 * ─── What the panel shows that the markers cannot ───────────────────────────
 * The `!`/`?` glyphs (render/quest-markers.ts) answer "is there anything here",
 * which is a wayfinding question. This answers the bookkeeping ones: what each
 * step actually wants, how far along it is, what is still owed at turn-in, and
 * what it pays. `outstanding` is the load-bearing one — turn-in is
 * all-or-nothing server-side (it throws ITEMS_MISSING and consumes nothing),
 * so a player who cannot see they are one splint short just gets a red line
 * and no explanation.
 *
 * ─── Active only, no "On Offer" section ─────────────────────────────────────
 * This used to also list `board.offerable` in a second column. That duplicated
 * the `!` marker and the conversation option that already offer a step — this
 * panel now shows only what the player has actually taken on
 * (`board.active`), laid out as a horizontal row of cards rather than a
 * vertical stack.
 *
 * ─── Field notes: derived, not authored ─────────────────────────────────────
 * Below the active row is a block of short, first-person flavour lines — "I
 * should go find X" — one per active quest that has an outstanding NPC-facing
 * action right now. There is no server-side hint field for this; it is
 * computed from `claimable` and each objective's `kind`/`complete`, exactly
 * the fields `render/quest-markers.ts` already reads to decide gold-`!`
 * vs grey-`?`:
 *   - `claimable` → the reward is waiting, so the action is turning in to
 *     `turnInNpcKey`.
 *   - an incomplete `TALK` objective → the action is talking to
 *     `giverNpcKey`.
 *   - neither (e.g. a DELIVER step still short on items, nothing to say to
 *     anyone yet) → no line. A pure fetch-and-craft step has no NPC to visit,
 *     and a note claiming otherwise would send the player on a walk for
 *     nothing.
 * These two cases are mutually exclusive per quest: `claimable` requires every
 * objective complete, so a quest cannot have both a pending `TALK` objective
 * and be claimable at once — at most one line per quest.
 *
 * ─── Colour discipline ──────────────────────────────────────────────────────
 * Brass for progress and rewards, moss for done, rust for errors. `.hazard-band`
 * is NOT used anywhere here: the Codex reserves it for blocked/dangerous, and
 * "you have a quest" is neither.
 */
import {
  questStateStore, loadQuestBoard, loadJobBoard, turnInQuest, claimQuest, claimJob,
  cancelQuest, handOverGate, itemLabel,
} from '../../state/quest-state';
import { gameStateStore } from '../../state/game-state';
import type { InventorySheet } from '../../state/game-state.types';
import type {
  PeriodicQuestView, QuestState, StoryQuestView,
} from '../../state/quest-state.types';
import { closeOtherModals, registerPanel } from './panels';
import { createWindowChrome } from './window-chrome';
import { cashCoinHtml } from './coin';
import { BOUNTY_PREVIEW, itemIconUrl, jobKind } from './quest-art';

let unsubscribers: Array<() => void> = [];
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;

export function createQuestUI(): HTMLElement {
  // One shell for every window — see `ui/window-chrome.ts`. (`position:
  // relative` on the frame stays load-bearing: the close cross is positioned
  // against it, and `.panel` carries it.)
  const {
    root, panel, header, body,
  } = createWindowChrome({
    id: 'quest-ui',
    headerId: 'quest-header',
    bodyId: 'quest-body',
    width: 860,
    label: 'Close journal',
    onClose: () => closeQuestUI(),
    bodyClass: 'bv-quest-body',
  });

  // The title sits ABOVE the framed window, over the scrim, not inside it.
  // Written once: it never changes, so render() leaves it alone. Built here
  // rather than in window-chrome.ts, which every other window shares.
  const heading = document.createElement('div');
  heading.className = 'bv-q-wintitle';
  const headingText = document.createElement('h2');
  headingText.className = 'bv-q-wintitle-text';
  headingText.textContent = 'Field Journal';
  const headingSub = document.createElement('p');
  headingSub.className = 'bv-q-wintitle-sub';
  headingSub.textContent = 'Jobs taken';
  heading.append(headingText, headingSub);
  root.insertBefore(heading, panel);

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && root.style.display !== 'none') {
      e.preventDefault();
      closeQuestUI();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    if (root.style.display === 'none') return;
    render(header, body, questStateStore.get());
  };
  unsubscribers = [questStateStore.subscribe(rerender), gameStateStore.subscribe(rerender)];


  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'quest', isOpen: isQuestUIOpen, close: closeQuestUI });
  return root;
}

export function openQuestUI(): void {
  closeOtherModals('quest');
  const el = document.getElementById('quest-ui');
  if (el) el.style.display = 'flex';
  void loadQuestBoard();
  // Separate read, separate failure: a job board that 500s must not blank the
  // story quests. It also ASSIGNS the period's jobs when the player has none —
  // opening the journal is how a new player gets their first errands, because
  // core's reset cron only ever re-assigns to players who already hold one.
  void loadJobBoard();
}

/** Lets index.ts avoid stacking a dialog or the map behind an open journal. */
export function isQuestUIOpen(): boolean {
  const el = document.getElementById('quest-ui');
  return !!el && el.style.display !== 'none';
}

export function closeQuestUI(): void {
  const el = document.getElementById('quest-ui');
  if (el) el.style.display = 'none';
}

export function disposeQuestUI(): void {
  unregisterPanel?.();
  unregisterPanel = null;
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
}

// ─── rendering ──────────────────────────────────────────────────────────────
//
// Every piece of text sits in a container (a plate, a chip, a slip, a stamp) and
// every container has depth: bevels, rivets, a drop shadow, a hover lift. The
// look is in `styles/quest.css.ts`; this file only builds the classed markup,
// and entrance animation is a CSS variable (`--i`) per card so the cards stagger.

const ICON = {
  check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
  scroll: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><path d="M9 9h6M9 13h6M9 16h3"/></svg>',
  clock: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>',
  star: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/></svg>',
  box: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8l8-4 8 4v8l-8 4-8-4z"/><path d="M4 8l8 4 8-4M12 12v8"/></svg>',
  pin: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4h6l-1 6 3 3H7l3-3z"/><path d="M12 13v7"/></svg>',
  flag: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 21V4M6 5h11l-2 4 2 4H6"/></svg>',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function withIcon(cls: string, icon: string, text: string): HTMLElement {
  const n = el('span', cls);
  const i = el('span', 'bv-q-ico');
  i.innerHTML = icon;
  n.append(i, el('span', 'bv-q-txt', text));
  return n;
}

function render(header: HTMLElement, body: HTMLElement, state: QuestState): void {
  header.innerHTML = '';
  body.innerHTML = '';

  header.innerHTML = `
    ${state.error ? `<p class="bv-win-err">${escapeHtml(state.error)}</p>` : ''}
    ${state.lastResult ? `<p class="bv-win-ok">${escapeHtml(state.lastResult)}</p>` : ''}
  `;

  if (!state.board) {
    body.appendChild(note(state.busy ? 'Reading the journal…' : 'Journal unavailable.'));
    return;
  }

  body.appendChild(summaryStrip(state));
  body.appendChild(activeSection(state));
  body.appendChild(jobsSection(state));
  body.appendChild(notesSection(state));
}

/** Three gauges across the top: what is held, what is waiting to be paid, what is posted. */
function summaryStrip(state: QuestState): HTMLElement {
  const active = state.board?.active ?? [];
  const jobs = [...(state.jobs?.daily ?? []), ...(state.jobs?.weekly ?? [])];
  const ready = active.filter((q) => q.claimable).length + jobs.filter((j) => j.claimable).length;
  const strip = el('div', 'bv-q-strip');
  const gauge = (label: string, value: number, tone: string, icon: string): HTMLElement => {
    const g = el('div', `bv-q-gauge bv-q-gauge-${tone}`);
    const ico = el('span', 'bv-q-gauge-ico'); ico.innerHTML = icon;
    const num = el('span', 'bv-q-gauge-num', String(value));
    g.append(ico, num, el('span', 'bv-q-gauge-label', label));
    return g;
  };
  strip.append(
    gauge('In hand', active.length, 'brass', ICON.scroll),
    gauge('Ready to collect', ready, 'moss', ICON.flag),
    gauge('Odd jobs', jobs.length, 'steel', ICON.pin),
  );
  return strip;
}

/**
 * The settlement's casual job board — the repeating errands the NPCs post.
 *
 * Kept visually distinct from the story quests above it and placed BELOW them:
 * the chain is what the player is following, and a board of five errands at the
 * top would bury it. Each row names its giver, because "sell 5 items" with no
 * face attached is a checklist, not a settlement.
 */
function jobsSection(state: QuestState): HTMLElement {
  const jobs = state.jobs;
  const all = [...(jobs?.daily ?? []), ...(jobs?.weekly ?? [])];
  const section = el('section', 'bv-q-section');
  section.appendChild(sectionHeading('Odd Jobs', all.length, 'jobs', 'Errands posted on the board'));

  if (all.length === 0) {
    section.appendChild(note(state.busy ? 'Reading the board…' : 'Nothing posted right now.'));
    return section;
  }

  const grid = el('div', 'bv-q-jobs');
  all.forEach((job, i) => grid.appendChild(jobRow(job, state.busy, i)));
  section.appendChild(grid);

  if (jobs) {
    const resets = withIcon('bv-q-reset', ICON.clock, `New jobs ${untilLabel(jobs.dailyResetsAt)}`);
    resets.classList.add('bv-win-note');
    section.appendChild(resets);
  }
  return section;
}

function jobRow(job: PeriodicQuestView, busy: boolean, index: number): HTMLElement {
  const row = el('article', 'bv-q-job');
  row.dataset.job = job.slug;
  row.style.setProperty('--i', String(index));
  if (job.claimable) row.classList.add('bv-q-ready');
  if (job.status === 'CLAIMED') row.classList.add('bv-q-paid');

  const kind = jobKind(job.action);
  row.dataset.kind = kind;
  const art = jobArt(kind, job);
  if (art) row.appendChild(art); else row.classList.add('bv-q-job-noart');

  const main = el('div', 'bv-q-job-main');
  main.appendChild(el('h4', 'bv-q-job-title', job.period === 'WEEKLY' ? `${job.name} — weekly` : job.name));
  main.appendChild(giverChip(job.giverName));
  if (job.flavour) main.appendChild(el('p', 'bv-q-quote', `“${job.flavour}”`));
  main.appendChild(meter(job.description, job.progress, job.targetCount));
  row.appendChild(main);

  const side = el('div', 'bv-q-job-side');
  side.appendChild(rewardTray(job.reward.soft, job.reward.xp, 0, job.reward.items));
  if (job.claimable) {
    side.appendChild(button('Collect', 'moss', busy, () => { void claimJob(job.userQuestId); }));
  } else if (job.status === 'CLAIMED') {
    side.appendChild(el('span', 'bv-q-stamp bv-q-stamp-paid', 'paid'));
  }
  row.appendChild(side);
  return row;
}

/**
 * The job's picture: the ACTUAL item it pays, floating over a soft shadow, and
 * nothing else. Janeks: *"remove [the icons]. Keep only the actual item
 * mentioned. The background also can be removed, keep a shadow only."*
 *
 * A bounty also shows the hunted face (a character, not an icon). A job whose
 * rewards have no art returns null and the card drops the column entirely,
 * rather than inventing a stand-in picture.
 */
function jobArt(kind: ReturnType<typeof jobKind>, job: PeriodicQuestView): HTMLElement | null {
  // The first reward item that HAS art: a few catalog items (canned beans, for
  // one) ship without an icon, and floating nothing would hide a real reward.
  const shown = job.reward.items.find((i) => itemIconUrl(i.itemKey));
  if (!shown && kind !== 'bounty') return null;

  const art = el('div', `bv-q-art bv-q-art-${kind}`);
  if (kind === 'bounty') {
    const frame = el('div', 'bv-q-target');
    const img = el('img', 'bv-q-target-img');
    img.src = BOUNTY_PREVIEW;
    img.alt = '';
    frame.appendChild(img);
    art.appendChild(frame);
  }
  if (shown) {
    const wrap = el('div', 'bv-q-item');
    const float = el('img', 'bv-q-float');
    float.src = itemIconUrl(shown.itemKey)!;
    float.alt = '';
    float.title = itemLabel(shown.itemKey);
    wrap.append(float, el('span', 'bv-q-shadow'));
    art.appendChild(wrap);
  }
  return art;
}

/**
 * "in 4h" / "in 12m" until the next reset.
 *
 * Derived from the server's absolute timestamp and the wall clock, never from a
 * countdown the client keeps — the journal can sit open for hours, and the
 * board is re-read when it reopens.
 */
export function untilLabel(iso: string, now: number = Date.now()): string {
  const ms = Date.parse(iso) - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'shortly';
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `in ${hours}h`;
  return `in ${Math.max(1, Math.round(ms / 60_000))}m`;
}

function activeSection(state: QuestState): HTMLElement {
  const active = state.board?.active ?? [];
  const section = el('section', 'bv-q-section');
  section.appendChild(sectionHeading('In Hand', active.length, 'inhand', 'Work you have taken on'));
  if (active.length === 0) {
    section.appendChild(note('Nothing on the go. Ask around — the ! marks know.'));
    return section;
  }
  const grid = el('div', 'bv-q-cards');
  const inventory = gameStateStore.get().inventory;
  active.forEach((quest, i) => grid.appendChild(activeCard(quest, state.busy, inventory, i)));
  section.appendChild(grid);
  return section;
}

/**
 * One line per active quest with an outstanding NPC-facing action right now.
 * See the file-header comment for the exact rule; this is deliberately a
 * plain array-of-strings rather than DOM so the rule stays testable without
 * touching layout.
 */
function activeQuestNotes(active: readonly StoryQuestView[]): string[] {
  const lines: string[] = [];
  for (const quest of active) {
    if (quest.claimable) {
      lines.push(`I should visit ${quest.turnInNpcKey} — I think they're waiting on what I brought back.`);
      continue;
    }
    const needsTalk = quest.objectives.some((o) => o.kind === 'TALK' && !o.complete);
    if (needsTalk) {
      lines.push(`I should visit ${quest.giverNpcKey}, I think they have some work for me.`);
    }
    // Otherwise: a DELIVER step still short on items has nobody to visit yet.
  }
  return lines;
}

function notesSection(state: QuestState): HTMLElement {
  const lines = activeQuestNotes(state.board?.active ?? []);
  const section = el('section', 'bv-q-section');
  section.appendChild(sectionHeading('Notes to Self', lines.length, 'notes', 'Where to go next'));
  if (lines.length === 0) {
    section.appendChild(note('Nothing on my mind — the ledger above says it all.'));
    return section;
  }
  const list = el('div', 'bv-q-slips');
  lines.forEach((line, i) => {
    const slip = el('div', 'bv-q-slip');
    slip.style.setProperty('--i', String(i));
    slip.style.setProperty('--tilt', `${(i % 2 === 0 ? -1 : 1) * (0.6 + (i % 3) * 0.4)}deg`);
    const pin = el('span', 'bv-q-pushpin'); pin.innerHTML = ICON.pin;
    slip.append(pin, el('p', 'bv-q-slip-text', line));
    list.appendChild(slip);
  });
  section.appendChild(list);
  return section;
}

function activeCard(quest: StoryQuestView, busy: boolean, inventory: InventorySheet | null, index: number): HTMLElement {
  const card = el('article', 'bv-q-card');
  card.dataset.slug = quest.slug;
  card.style.setProperty('--i', String(index));
  if (quest.claimable) card.classList.add('bv-q-ready');

  // Rivets are four real elements, not a background, so each can be a tiny
  // raised dome with its own highlight.
  for (const corner of ['tl', 'tr', 'bl', 'br']) card.appendChild(el('i', `bv-q-rivet bv-q-rivet-${corner}`));

  const top = el('header', 'bv-q-card-top');
  top.appendChild(el('h4', 'bv-q-card-title', quest.name));
  top.appendChild(el('span', 'bv-q-step', `${quest.completed}/${quest.total}`));
  card.appendChild(top);

  card.appendChild(giverChip(`Report to ${quest.turnInNpcKey}`));
  if (quest.claimable) card.appendChild(el('span', 'bv-q-stamp bv-q-stamp-ready', 'ready'));
  card.appendChild(el('p', 'bv-q-brief', quest.description));

  const list = el('ul', 'bv-q-objs');
  quest.objectives.forEach((obj, i) => {
    const li = el('li', `bv-q-obj ${obj.complete ? 'bv-q-obj-done' : ''}`);
    li.style.setProperty('--j', String(i));
    const box = el('span', 'bv-q-checkbox'); box.innerHTML = ICON.check;
    const label = el('span', 'bv-q-obj-label', obj.description);
    const count = el('span', 'bv-q-obj-count', `${obj.progress}/${obj.targetCount}`);
    const bar = el('span', 'bv-q-bar'); const fill = el('span', 'bv-q-fill');
    fill.style.setProperty('--p', String(obj.targetCount > 0 ? Math.min(1, obj.progress / obj.targetCount) : 0));
    bar.appendChild(fill);
    li.append(box, label, count, bar);
    list.appendChild(li);
  });
  card.appendChild(list);

  const gate = quest.claimable ? null : handOverGate(quest, inventory);
  if (gate?.reason) {
    const owed = el('p', 'bv-q-owed', gate.reason);
    card.appendChild(owed);
  }

  card.appendChild(rewardTray(quest.reward.soft, quest.reward.xp, quest.reward.reputation, quest.reward.items));

  const actions = el('div', 'bv-q-actions');
  if (quest.claimable) {
    actions.appendChild(button('Collect', 'moss', busy, () => { void claimQuest(quest.slug); }));
  } else {
    // Locked until the hideout actually holds what the step asks for. The gate
    // reads the live inventory, so it can be wrong only by being a moment
    // stale - and the server still decides: it re-checks everything on turn-in.
    actions.appendChild(button(
      'Hand over', 'brass', busy || !gate?.enabled, () => { void turnInQuest(quest.slug); },
      gate?.reason ?? undefined,
    ));
  }
  actions.appendChild(button('Abandon', 'quiet', busy, () => { void cancelQuest(quest.slug); }));
  card.appendChild(actions);

  return card;
}

// ─── small parts ────────────────────────────────────────────────────────────

/**
 * A banner, not a bare heading: Higgsfield art on the right (one picture per
 * group, see /public/clutter/banner-*.webp), the title and a caption on the
 * dark left, the count in a brass badge.
 */
function sectionHeading(text: string, count: number | undefined, art: string, caption: string): HTMLElement {
  const h = el('h3', `bv-q-heading bv-q-heading-${art}`);
  const words = el('span', 'bv-q-heading-words');
  words.append(el('span', 'bv-q-heading-text', text), el('span', 'bv-q-heading-cap', caption));
  h.appendChild(words);
  if (count !== undefined) h.appendChild(el('span', 'bv-q-heading-count', String(count)));
  return h;
}

/** The giver as a chip with an initial disc — a face, in lieu of a portrait asset. */
function giverChip(who: string): HTMLElement {
  const chip = el('span', 'bv-q-giver');
  const name = who.replace(/^Report to /, '');
  chip.append(el('span', 'bv-q-giver-disc', (name.charAt(0) || '?').toUpperCase()), el('span', 'bv-q-giver-name', who));
  return chip;
}

/** One progress readout: the task, a count, and a bar that fills in. */
function meter(task: string, progress: number, target: number): HTMLElement {
  const m = el('div', 'bv-q-meter');
  const row = el('div', 'bv-q-meter-row');
  row.append(el('span', 'bv-q-meter-task', task), el('span', 'bv-q-obj-count', `${progress}/${target}`));
  const bar = el('span', 'bv-q-bar'); const fill = el('span', 'bv-q-fill');
  fill.style.setProperty('--p', String(target > 0 ? Math.min(1, progress / target) : 0));
  bar.appendChild(fill);
  m.append(row, bar);
  return m;
}

/** What it pays, as chips: the coin, XP, standing, and each item. */
function rewardTray(soft: number, xp: number, rep: number, items: ReadonlyArray<{ itemKey: string; qty: number }>): HTMLElement {
  const tray = el('div', 'bv-q-pays');
  tray.appendChild(el('span', 'bv-q-pays-label', 'Pays'));
  const cash = el('span', 'bv-q-chip bv-q-chip-cash');
  cash.innerHTML = `${cashCoinHtml('md')}<span class="bv-q-txt">${soft} Cash</span>`;
  tray.appendChild(cash);
  tray.appendChild(withIcon('bv-q-chip bv-q-chip-xp', ICON.star, `${xp} XP`));
  if (rep) tray.appendChild(withIcon('bv-q-chip bv-q-chip-rep', ICON.flag, `${rep} rep`));
  for (const item of items) tray.appendChild(withIcon('bv-q-chip bv-q-chip-item', ICON.box, `${item.qty}x ${itemLabel(item.itemKey)}`));
  return tray;
}

type ButtonTone = 'brass' | 'moss' | 'quiet';

/**
 * Brass = spend/commit, moss = collect, quiet = the destructive-but-boring
 * one. Abandon is deliberately the QUIETEST control rather than a red alarm:
 * cancelling keeps every granted item and leaves the step re-offerable, so
 * dressing it as danger would overstate it.
 */
function button(
  label: string, tone: ButtonTone, off: boolean, onClick: () => void, why?: string,
): HTMLElement {
  const b = el('button', `bv-q-btn bv-q-btn-${tone}`, label);
  b.type = 'button';
  b.disabled = off;
  if (off && why) b.title = why;
  // Inline as well as in the stylesheet: the 44px tap target is asserted from
  // the element, and a stylesheet that fails to load must not shrink it.
  b.style.minHeight = '44px';
  b.onclick = onClick;
  return b;
}

/** An empty state is a container too — a dashed slot with an icon, never loose text. */
function note(text: string): HTMLElement {
  const p = el('p', 'bv-win-empty bv-q-empty');
  const i = el('span', 'bv-q-empty-ico'); i.innerHTML = ICON.scroll;
  p.append(i, el('span', 'bv-q-txt', text));
  return p;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}
